import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { isLightClubBackground, normalizeHexColor } from '../../utils/clubProductexContrast';
import { useClienteAuth } from '../../context/ClienteAuthContext';
import {
  Crown,
  Star,
  Award,
  Zap,
  Gift,
  RefreshCw,
  TrendingUp,
  Clock,
  CheckCircle,
  Package,
  Sparkles,
  ArrowRight,
  X,
  Check
} from 'lucide-react';

interface PointsSummary {
  client_id: string;
  points_balance: number;
  points_earned: number;
  points_spent: number;
  total_earned_lifetime: number;
  current_level: string;
  level_info: {
    name: string;
    min_points: number;
    max_points: number;
    color_primary: string;
    color_secondary: string;
    benefits: string[];
  };
  next_level?: {
    name: string;
    min_points: number;
    points_needed: number;
  };
  breakdown?: {
    invoice_points: number;
    credit_note_points?: number;
    production_points: number;
    express_points: number;
    sample_points: number;
  };
  peso_rate_used?: number;
  points_per_1000_pesos?: number;
  last_updated: string;
}

interface RedemptionItem {
  id: string;
  name: string;
  description: string;
  points_cost: number;
  category: string;
  is_active: boolean;
}

interface ClientRedemption {
  id: string;
  redemption_id: string;
  points_spent: number;
  status: string;
  notes?: string;
  created_at: string;
  processed_at?: string;
  redemption: {
    name: string;
    description: string;
    category: string;
  };
}

const ClubProductex: React.FC = () => {
  const { cliente } = useClienteAuth();
  const [pointsData, setPointsData] = useState<PointsSummary | null>(null);
  const [redemptions, setRedemptions] = useState<RedemptionItem[]>([]);
  const [clientRedemptions, setClientRedemptions] = useState<ClientRedemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [selectedRedemption, setSelectedRedemption] = useState<RedemptionItem | null>(null);
  const [realClientId, setRealClientId] = useState<string | null>(null);
  const [pointsHistory, setPointsHistory] = useState<Array<{ fecha: string; tipo: string; descripcion: string; puntos: number }>>([]);
  const [resultModal, setResultModal] = useState<{ show: boolean; success: boolean; message: string }>({ show: false, success: false, message: '' });

  useEffect(() => {
    if (cliente) {
      loadPointsData();
      loadRedemptions();
      loadPointsHistory();
    }
  }, [cliente]);

  const loadPointsHistory = async () => {
    if (!cliente?.id) return;
    try {
      const { data, error } = await supabase.rpc('get_client_points_history', { p_portal_cliente_id: cliente.id });
      if (!error && data) setPointsHistory(Array.isArray(data) ? data : []);
    } catch {
      setPointsHistory([]);
    }
  };

  useEffect(() => {
    if (realClientId) {
      loadClientRedemptions();
    }
  }, [realClientId]);

  const loadPointsData = async () => {
    try {
      console.log('Loading points data for cliente:', cliente?.id);

      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('id')
        .eq('portal_cliente_id', cliente.id)
        .maybeSingle();

      console.log('Client lookup result:', { clientData, clientError });

      if (clientError) {
        console.error('Error looking up client:', clientError);
        throw clientError;
      }

      if (!clientData) {
        console.error('No client found with portal_cliente_id:', cliente.id);
        throw new Error('Cliente no encontrado en el sistema');
      }

      setRealClientId(clientData.id);
      console.log('Real client ID set to:', clientData.id);

      const { data, error } = await supabase.rpc('get_client_points_breakdown', {
        p_client_id: clientData.id
      });

      console.log('Points breakdown result:', { data, error });

      if (error) {
        console.error('Error getting points breakdown:', error);
        throw error;
      }

      if (!data) {
        console.error('No data returned from get_client_points_breakdown');
        throw new Error('No se pudieron obtener los puntos');
      }

      const levelInfo = data.current_level || {
        name: 'Starter',
        min_points: 0,
        max_points: 499,
        color_primary: '#10B981',
        color_secondary: '#FFFFFF',
        benefits: ['Acceso al catálogo de canjes', 'Acumulación de puntos', 'Soporte prioritario']
      };
      const benefits = Array.isArray(levelInfo.benefits) && levelInfo.benefits.length > 0
        ? levelInfo.benefits
        : ['Acceso al catálogo de canjes', 'Acumulación de puntos por compras', 'Soporte prioritario'];

      const transformedData = {
        client_id: data.client_id,
        points_balance: data.points_balance ?? 0,
        points_earned: data.total_points ?? 0,
        points_spent: data.points_spent ?? 0,
        total_earned_lifetime: data.total_points ?? 0,
        current_level: levelInfo.name || 'Starter',
        level_info: { ...levelInfo, benefits },
        next_level: data.next_level,
        breakdown: data.breakdown || {
          invoice_points: 0,
          credit_note_points: 0,
          production_points: 0,
          express_points: 0,
          sample_points: 0
        },
        peso_rate_used: data.peso_rate_used || 1,
        points_per_1000_pesos: data.points_per_1000_pesos || 1,
        last_updated: data.calculation_date || new Date().toISOString()
      };

      console.log('Transformed data:', transformedData);
      setPointsData(transformedData);

      try {
        await supabase.rpc('log_cliente_activity', {
          p_cliente_id: cliente.id,
          p_action: 'Visualización de Club Productex',
          p_entity_type: 'club_productex',
          p_details: `Cliente visualizó Club Productex con ${transformedData.points_balance} puntos`,
          p_action_type: 'view'
        });
      } catch (logError) {
        console.warn('Error logging activity:', logError);
      }
    } catch (error) {
      console.error('Error loading points data:', error);
      alert(`Error al cargar puntos: ${error instanceof Error ? error.message : 'Error desconocido'}`);
      setLoading(false);
    }
  };

  const loadRedemptions = async () => {
    try {
      let data: RedemptionItem[] | null = null;
      const { data: tableData, error: tableError } = await supabase
        .from('club_productex_redemptions')
        .select('*')
        .eq('is_active', true)
        .order('points_cost', { ascending: true });

      if (!tableError && tableData?.length) {
        data = tableData;
      } else {
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_portal_club_redemptions');
        if (!rpcError && rpcData) {
          data = Array.isArray(rpcData) ? rpcData : [];
        }
      }
      setRedemptions(data || []);
    } catch (error) {
      console.error('Error loading redemptions:', error);
      setRedemptions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadClientRedemptions = async () => {
    if (!realClientId) return;

    try {
      const { data, error } = await supabase
        .from('club_productex_client_redemptions')
        .select(`
          *,
          redemption:club_productex_redemptions(
            name,
            description,
            category
          )
        `)
        .eq('client_id', realClientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClientRedemptions(data || []);
    } catch (error) {
      console.error('Error loading client redemptions:', error);
    }
  };

  const handleRedemption = async (redemptionId: string, pointsCost: number) => {
    if (!pointsData || pointsData.points_balance < pointsCost) {
      alert('No tienes suficientes puntos para este canje');
      return;
    }

    if (!cliente?.id) {
      alert('Error: No se pudo identificar tu sesión');
      return;
    }

    try {
      const { data, error } = await supabase.rpc('portal_redeem_benefit', {
        p_portal_cliente_id: cliente.id,
        p_redemption_id: redemptionId,
        p_points_cost: pointsCost
      });

      if (error) throw error;

      const result = typeof data === 'object' ? data : (data ? JSON.parse(data) : null);
      if (result && result.success === false) {
        throw new Error(result.error || 'Error al procesar el canje');
      }

      try {
        await supabase.rpc('log_cliente_activity', {
          p_cliente_id: cliente.id,
          p_action: 'Canje de puntos',
          p_entity_type: 'redemption',
          p_entity_id: redemptionId,
          p_details: `Cliente canjeó ${pointsCost} puntos por producto`,
          p_action_type: 'create'
        });
      } catch (logError) {
        console.warn('Error logging activity:', logError);
      }

      setShowRedemptionModal(false);
      setSelectedRedemption(null);
      setResultModal({ show: true, success: true, message: '¡Canje solicitado exitosamente! Te contactaremos pronto para coordinar la entrega.' });

      await loadPointsData();
      await loadClientRedemptions();
      await loadPointsHistory();
    } catch (error) {
      console.error('Error processing redemption:', error);
      setResultModal({ show: true, success: false, message: 'Error al procesar el canje. Por favor intenta nuevamente.' });
    }
  };

  const handleRecalculatePoints = async () => {
    try {
      setLoading(true);

      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('id')
        .eq('portal_cliente_id', cliente.id)
        .single();

      if (clientError) throw clientError;
      if (!clientData) throw new Error('Cliente no encontrado');

      const { error } = await supabase.rpc('update_client_points', {
        p_client_id: clientData.id
      });

      if (error) throw error;

      try {
        await supabase.rpc('log_cliente_activity', {
          p_cliente_id: cliente.id,
          p_action: 'Recálculo de puntos',
          p_entity_type: 'club_productex',
          p_details: 'Cliente solicitó recálculo de puntos',
          p_action_type: 'update'
        });
      } catch (logError) {
        console.warn('Error logging activity:', logError);
      }

      await loadPointsData();
      setResultModal({ show: true, success: true, message: 'Puntos recalculados exitosamente' });
    } catch (error) {
      console.error('Error recalculating points:', error);
      setResultModal({ show: true, success: false, message: 'Error al recalcular puntos' });
    } finally {
      setLoading(false);
    }
  };

  const getLevelIcon = (levelName: string) => {
    switch (levelName.toLowerCase()) {
      case 'starter': return <Zap className="w-8 h-8" />;
      case 'gold': return <Star className="w-8 h-8" />;
      case 'silver': return <Award className="w-8 h-8" />;
      case 'black': return <Crown className="w-8 h-8" />;
      default: return <Star className="w-8 h-8" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'descuentos': return <TrendingUp className="w-5 h-5" />;
      case 'productos': return <Package className="w-5 h-5" />;
      case 'servicios': return <Sparkles className="w-5 h-5" />;
      case 'experiencias': return <Star className="w-5 h-5" />;
      default: return <Gift className="w-5 h-5" />;
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      'descuentos': 'Descuentos',
      'productos': 'Productos',
      'servicios': 'Servicios',
      'experiencias': 'Experiencias',
      'otros': 'Otros'
    };
    return labels[category] || category;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'pending': 'Pendiente',
      'processing': 'Procesando',
      'completed': 'Entregado',
      'cancelled': 'Cancelado',
      'rejected': 'Rechazado'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'processing': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredRedemptions = selectedCategory === 'all'
    ? redemptions
    : redemptions.filter(item => item.category === selectedCategory);

  const categories = ['all', ...new Set(redemptions.map(item => item.category))];

  const {
    levelColor,
    heroLightBg,
    heroText,
    heroMuted,
    heroMuted2,
    heroIconBox,
    heroBorder,
    heroProgressTrack,
    heroProgressFill,
  } = useMemo(() => {
    const lc = normalizeHexColor(pointsData?.level_info?.color_primary);
    const light = isLightClubBackground(lc);
    return {
      levelColor: lc,
      heroLightBg: light,
      heroText: light ? 'text-gray-900' : 'text-white',
      heroMuted: light ? 'text-gray-600' : 'text-white/80',
      heroMuted2: light ? 'text-gray-700' : 'text-white/90',
      heroIconBox: light ? 'bg-black/10 text-gray-900' : 'bg-white/25 backdrop-blur-sm text-white',
      heroBorder: light ? 'border-gray-900/15' : 'border-white/20',
      heroProgressTrack: light ? 'bg-black/10' : 'bg-white/20',
      heroProgressFill: light ? 'bg-gray-900' : 'bg-white',
    };
  }, [pointsData?.level_info?.color_primary]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Cargando Club Productex...</p>
        </div>
      </div>
    );
  }

  if (!pointsData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="rounded-2xl bg-white border border-gray-200 shadow-lg p-8 text-center max-w-md">
          <Crown className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Club Productex</h2>
          <p className="text-gray-600 mb-4">No se pudo cargar tu información de puntos.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-gray-900 font-medium"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header compacto */}
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-2xl shadow-lg bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600">
            <Crown className="w-10 h-10 text-white drop-shadow" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Club Productex</h1>
            <p className="text-gray-600 text-sm">Programa de lealtad exclusivo</p>
          </div>
        </div>

        {/* Hero compacto: nivel + puntos en una sola fila */}
        <div
          className={`rounded-2xl p-6 md:p-8 shadow-2xl overflow-hidden relative ${heroText}`}
          style={{ background: `linear-gradient(135deg, ${levelColor} 0%, ${levelColor}cc 50%, ${levelColor}99 100%)` }}
        >
          <div
            className={`absolute inset-0 ${
              heroLightBg
                ? 'bg-[radial-gradient(circle_at_30%_20%,rgba(0,0,0,0.06)_0%,transparent_50%)]'
                : 'bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.2)_0%,transparent_50%)]'
            }`}
          />
          <div className="relative flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className={`p-5 rounded-2xl shadow-inner ${heroIconBox}`}>
                {getLevelIcon(pointsData.current_level)}
              </div>
              <div>
                <p className={`${heroMuted} text-xs font-medium uppercase tracking-wider`}>Tu nivel</p>
                <h2 className="text-3xl md:text-4xl font-black">{pointsData.current_level}</h2>
                <p className={`${heroMuted2} text-sm mt-1`}>
                  {pointsData.level_info.min_points.toLocaleString()} – {pointsData.level_info.max_points ?? '∞'} pts
                </p>
              </div>
            </div>
            <div className="text-center sm:text-right">
              <p className={`${heroMuted} text-xs font-medium uppercase tracking-wider`}>Disponibles</p>
              <p className="text-5xl md:text-6xl font-black tracking-tight">{pointsData.points_balance.toLocaleString()}</p>
              <p className={`${heroMuted2} text-sm`}>puntos para canjear</p>
            </div>
          </div>
          {pointsData.next_level && pointsData.next_level.points_needed > 0 && (
            <div className={`mt-6 pt-4 border-t ${heroBorder}`}>
              <div className={`flex justify-between text-sm mb-2 ${heroMuted2}`}>
                <span>Próximo: {pointsData.next_level.name}</span>
                <span>{pointsData.next_level.points_needed.toLocaleString()} pts restantes</span>
              </div>
              <div className={`h-2 rounded-full overflow-hidden ${heroProgressTrack}`}>
                <div
                  className={`h-full rounded-full transition-all duration-500 ${heroProgressFill}`}
                  style={{
                    width: `${Math.min(100, Math.max(0, ((pointsData.points_earned - pointsData.level_info.min_points) / (pointsData.next_level.min_points - pointsData.level_info.min_points)) * 100))}%`
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Beneficios + stats en grid compacto */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 rounded-2xl bg-white border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Beneficios de tu nivel
            </h3>
            <div className="flex flex-wrap gap-2">
              {pointsData.level_info.benefits?.map((benefit, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-100 text-gray-800 text-sm"
                >
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  {benefit}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-5 space-y-4">
            <p className="text-gray-500 text-xs uppercase tracking-wider">Resumen</p>
            <div>
              <p className="text-2xl font-bold text-gray-900">{pointsData.points_earned.toLocaleString()}</p>
              <p className="text-gray-500 text-xs">Ganados</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{pointsData.points_spent.toLocaleString()}</p>
              <p className="text-gray-500 text-xs">Canjeados</p>
            </div>
            <div className="pt-2 border-t border-gray-100">
              <p className="text-sm text-gray-600">$1.000 = 1 punto</p>
            </div>
          </div>
        </div>

        {/* Nota: Nivel según acumulado 12 meses */}
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
          <p className="text-sm text-amber-900">
            <strong>¿Cómo se calcula tu nivel?</strong> Tu membresía se determina por el <strong>acumulado total de puntos de los últimos 12 meses</strong> (ventana móvil).
            Los canjes <strong>no reducen tu nivel</strong>: solo restan puntos disponibles para canjear.
          </p>
        </div>

        {/* Historial de puntos */}
        {pointsHistory.length > 0 && (
          <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-500" />
              Historial de Puntos
            </h2>
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-left border-b border-gray-200">
                    <th className="py-2 pr-4">Fecha</th>
                    <th className="py-2 pr-4">Tipo</th>
                    <th className="py-2 pr-4">Descripción</th>
                    <th className="py-2 text-right">Puntos</th>
                  </tr>
                </thead>
                <tbody>
                  {pointsHistory.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 pr-4 text-gray-700">{new Date(row.fecha).toLocaleDateString('es-AR')}</td>
                      <td className="py-2 pr-4">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          row.tipo === 'factura' ? 'bg-emerald-100 text-emerald-800' :
                          row.tipo === 'nota_credito' ? 'bg-red-100 text-red-800' :
                          row.tipo === 'canje' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {row.tipo === 'factura' ? 'Factura' : row.tipo === 'nota_credito' ? 'N. Crédito' : row.tipo === 'canje' ? 'Canje' : 'Ajuste'}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-gray-700">{row.descripcion}</td>
                      <td className={`py-2 text-right font-medium ${row.puntos >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {row.puntos >= 0 ? '+' : ''}{row.puntos}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Catálogo de canjes */}
        <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Gift className="w-6 h-6 text-amber-500" />
                Catálogo de Canjes
              </h2>
              <p className="text-gray-600 text-sm mt-1">Canjea tus puntos por productos y beneficios exclusivos</p>
            </div>
            <button
              onClick={handleRecalculatePoints}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  selectedCategory === cat
                    ? 'bg-amber-500 text-gray-900'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat === 'all' ? 'Todos' : getCategoryLabel(cat)}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRedemptions.map((item) => (
              <div
                key={item.id}
                className="p-5 rounded-xl bg-gray-50 border border-gray-200 hover:border-amber-400 hover:bg-amber-50/50 cursor-pointer transition-all group"
                onClick={() => {
                  setSelectedRedemption(item);
                  setShowRedemptionModal(true);
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 rounded-lg bg-amber-100 text-amber-600">
                    {getCategoryIcon(item.category)}
                  </div>
                  <span className="px-2 py-1 rounded-full bg-gray-200 text-gray-600 text-xs capitalize">
                    {getCategoryLabel(item.category)}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.name}</h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{item.description}</p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-500" />
                    <span className="text-xl font-bold text-amber-600">{item.points_cost.toLocaleString()}</span>
                  </div>
                  <button
                    disabled={pointsData.points_balance < item.points_cost}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedRedemption(item);
                      setShowRedemptionModal(true);
                    }}
                    className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-gray-900 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Canjear
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredRedemptions.length === 0 && (
            <div className="text-center py-12">
              <Gift className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No hay productos disponibles en esta categoría</p>
            </div>
          )}
        </div>

        {clientRedemptions.length > 0 && (
          <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Mis Canjes Recientes
            </h2>
            <div className="space-y-2">
              {clientRedemptions.slice(0, 5).map((redemption) => (
                <div
                  key={redemption.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div>
                    <h3 className="font-semibold text-gray-900">{redemption.redemption?.name}</h3>
                    <p className="text-sm text-gray-600">{new Date(redemption.created_at).toLocaleDateString('es-AR')}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-amber-600">{redemption.points_spent.toLocaleString()} pts</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(redemption.status)}`}>
                      {getStatusLabel(redemption.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showRedemptionModal && selectedRedemption && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="max-w-md w-full rounded-2xl bg-white border border-gray-200 p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Confirmar Canje</h3>
              <button
                onClick={() => {
                  setShowRedemptionModal(false);
                  setSelectedRedemption(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 rounded-xl bg-gray-50 mb-4">
              <h4 className="font-bold text-lg text-gray-900 mb-2">{selectedRedemption.name}</h4>
              <p className="text-sm text-gray-600 mb-3">{selectedRedemption.description}</p>
              <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <span className="text-sm text-gray-600">Costo</span>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  <span className="text-2xl font-bold text-amber-600">{selectedRedemption.points_cost.toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-gray-50 mb-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Puntos actuales</span>
                <span className="font-bold text-gray-900">{pointsData.points_balance.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">A canjear</span>
                <span className="font-bold text-red-600">-{selectedRedemption.points_cost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-gray-600">Restantes</span>
                <span className="font-bold text-emerald-600">{(pointsData.points_balance - selectedRedemption.points_cost).toLocaleString()}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRedemptionModal(false);
                  setSelectedRedemption(null);
                }}
                className="flex-1 px-4 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleRedemption(selectedRedemption.id, selectedRedemption.points_cost)}
                disabled={pointsData.points_balance < selectedRedemption.points_cost}
                className="flex-1 px-4 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-gray-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              >
                <Check className="w-4 h-4" />
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de resultado (éxito/error) */}
      {resultModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full rounded-2xl p-6 shadow-2xl ${
            resultModal.success ? 'bg-white border border-emerald-200' : 'bg-white border border-red-200'
          }`}>
            <div className="text-center">
              {resultModal.success ? (
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              ) : (
                <X className="w-16 h-16 text-red-500 mx-auto mb-4" />
              )}
              <p className={`text-lg font-medium ${resultModal.success ? 'text-gray-800' : 'text-gray-800'}`}>
                {resultModal.message}
              </p>
              <button
                onClick={() => setResultModal({ ...resultModal, show: false })}
                className={`mt-6 px-6 py-3 rounded-xl font-medium transition-colors ${
                  resultModal.success
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubProductex;
